import { action, observable, runInAction } from 'mobx';
import ApiService from '@app/Services/ApiService';
import { ApiUrls, Permissions, Configurations } from '@app/AppConstants';
import { loaderStore } from '@app/Stores/LoaderStore';
import { configurationStore } from '@app/Stores/ConfigurationStore';
import { PermissionsResult } from '@app/Models';

class SecurityService {
    @observable private personPermissions: Permissions[] = [];
    @observable private originalPersonPermissions: Permissions[] = [];
    @observable private enablePermissionsCheck: boolean = true;
    @observable public enablePermissionsColoring: boolean = false;
    @observable public permissionsLoaded: boolean = false;

    @action
    async loadPermissions() {
        const [permissionsResponse, enablePermissionsCheck, enablePermissionsColoring] = await Promise.all([
            ApiService.getTypedData<PermissionsResult>(ApiUrls.PermissionUrl, null, { completion: loaderStore.globalLoader }),
            configurationStore.getGlobal(Configurations.featureGroup, Configurations.EnablePermissionsCheckKey),
            configurationStore.getGlobal(Configurations.featureGroup, Configurations.EnablePermissionsColoringKey)
        ]);

        runInAction(() => {
            this.permissionsLoaded = true;
            this.personPermissions = permissionsResponse?.data?.permissions ?? [];
            this.originalPersonPermissions = permissionsResponse?.data?.originalPermissions ?? [];
            this.enablePermissionsCheck = enablePermissionsCheck?.toLowerCase() === 'true';
            this.enablePermissionsColoring = enablePermissionsColoring?.toLowerCase()  === 'true';
        });
    }

    hasPermission = (permission: string, legacyValue?: boolean) => {
        if (!this.enablePermissionsCheck){
            return legacyValue ?? true;
        }
        return this._hasPermissionInternal(permission);
    };

    
    hasOriginalPermission = (permission: string) => {
        if (!this.enablePermissionsCheck){
            return false;
        }
        return this.originalPersonPermissions.findIndex(x => x === permission) !== -1;
    };

    private _hasPermissionInternal(permission: string) {
        return this.personPermissions.findIndex(x => x === permission) !== -1;
    }

    permissionViewCss = (readPermission: string) => {
        if (!this.enablePermissionsColoring){
            return '';
        }

        let result = '';
        if (readPermission && !this._hasPermissionInternal(readPermission)){
            result += ' security security-hidden';
        }

        return result;
    };

    permissionCss = (readPermission: string | null, editPermission?: string) => {
        if (!this.enablePermissionsColoring){
            return '';
        }
        let result = '';
        if (readPermission && !this._hasPermissionInternal(readPermission)){
            result += ' security security-hidden';
        }
        if (!result && editPermission && !this._hasPermissionInternal(editPermission)){
            result += ' security security-readonly';
        }
        if (!result && editPermission && this._hasPermissionInternal(editPermission)){
            result += ' security security-visible';
        }
        if (!result){
            result += ' security security-readonly';
        }

        return result;
    };

    hasAnyPermission = (permissions: string[]): boolean => {
        let result = false;
        permissions.forEach(permission => {
            if(this.personPermissions.findIndex(x => x === permission) !== -1)
                result = true;
        });
        return result;
    };
}

export const securityService = new SecurityService();